import React, {FunctionComponent, lazy, Suspense, useState} from "react";
import {makeStyles} from "tss-react/mui";
import CheckIcon from "@mui/icons-material/Check";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import {
    AppBar,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    Grid,
    IconButton,
    Theme,
    Toolbar,
    Tooltip,
    Typography,
} from "@mui/material";
import moment from "moment/moment";
import DispoAcceptBookingMutation from "@/Domains/DispositionManagement/Mutations/DispoAcceptBookingMutation";
import DispoRejectBookingMutation from "@/Domains/DispositionManagement/Mutations/DispoRejectBookingMutation";
import CloseIcon from "@mui/icons-material/Close";
import {CLOSE_BOOKING_INFO, FETCHING_DATA_START,} from "@/Redux/Disposition/Constants";
import {useAppDispatch, useAppSelector} from "@/Redux/Redux";
import {useDisplayMessage} from "@/Utils/CustomHooks/useDisplayMessage";
import {Sync} from "mdi-material-ui";
import {UndoOutlined, VpnKey} from "@mui/icons-material";
import {logger} from "@/Logger";
import {useSelector} from "react-redux";
import {selectViewerEnabledStations, ViewerState,} from "@/Redux/Auth/AuthReducer";
import {useHistory} from "react-router-dom";
import {BaseError} from "@/Utils/BaseError";
import {useTranslation} from "@/Utils/CustomHooks/useTranslation";
import {Environment, QueryRenderer} from "react-relay";
import {relayAuthEnvironment} from "../../../../RelayEnvironment";
import {graphql} from "relay-runtime";
import PreviousContractNotCheckedInModal
    from "@/Domains/DispositionManagement/Components/Modals/PreviousContractNotCheckedInModal";
import {
    BookingInfoModalQuery
} from "@/Domains/DispositionManagement/Components/Modals/__generated__/BookingInfoModalQuery.graphql";
import UndoCheckOutMutation from "@/Domains/BookingManagement/Booking/Shared/Mutations/UndoCheckOutMutation";
import UndoCheckInMutation from "@/Domains/BookingManagement/Booking/Shared/Mutations/UndoCheckInMutation";

const EmailModalWrapper = lazy(
    () =>
        import(
            "../../../CommunicationManagement/Containers/EmailModalWrapper"
            )
);

interface BookingInfoOverlayProps {
    predefinedStartdate?: string;
    predefinedBookingId?: string;
    viewData: any;
}

const useStyles = makeStyles()((theme: Theme) => ({
    buttonProgress: {
        position: "absolute",
        top: "54%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },

    wrapper: {
        marginTop: theme.spacing(0),
        position: "relative",
    },

    appBar: {
        position: "relative",
        boxShadow: "none",
    },

    title: {
        flex: 1,
    },
}));

const BookingInfoModal: FunctionComponent<
    BookingInfoOverlayProps
> = ({predefinedStartdate, predefinedBookingId, viewData}): JSX.Element => {
    const {classes} = useStyles();
    const {modules} = useAppSelector((state) => state.app);
    const dispatch = useAppDispatch();
    const [wizardAcceptBookingIsLoading, setWizardAcceptBookingIsLoading] =
        useState(false);
    const [wizardRejectBookingIsLoading, setWizardRejectBookingIsLoading] =
        useState(false);
    const {displayMessage} = useDisplayMessage();
    const {t} = useTranslation();
    const {selectedStation, dispositionStartdate, dispositionEnddate} =
        useAppSelector((state) => state.disposition);
    const enabledStations = useSelector((state) =>
        selectViewerEnabledStations(state as ViewerState)
    );
    const rights = viewData?.viewer?.rights;

    const [bookingCIModalIsOpen, setBookingCIModalIsOpen] = useState(false);

    const history = useHistory();


    const {bookingInfoOverlayItem} = useAppSelector(
        (state) => state.disposition
    );

    const checkoutIsAllowed =
        enabledStations.filter(
            (station: any) =>
                station.isChangeableInDispo == "J" &&
                (station.stationId ===
                    bookingInfoOverlayItem?.startstation?.stationId ||
                    station.stationId ===
                    bookingInfoOverlayItem?.startstation)
        ).length > 0;
    const [
        rejectReservationEmailModalIsOpen,
        setRejectReservationEmailModalIsOpen,
    ] = useState(false);
    const [
        acceptReservationEmailModalIsOpen,
        setAcceptReservationEmailModalIsOpen,
    ] = useState(false);

    logger.log("open booking info modal with booking", {
        bookingId: bookingInfoOverlayItem.bookingId,
        status: bookingInfoOverlayItem.status,
    });


    const isSpezRes = (resNo = "") => {
        const resNr = resNo;
        const re = /\d+\-\d+\-[A-Za-z]+\-\d/;
        if (resNr.match(re) !== null) return true;
        else return false;
    };
    const getHeaderText = (): string => {
        let text = "";
        const status = bookingInfoOverlayItem?.status;
        if (status === "reservation") {
            text = "Reservierung:";
        } else if (status === "transfer_co" || status === "transfer_ci") {
            text = "Transferfahrt:";
        } else if (status === "contract_co") {
            text = "Mietvertrag:";
        } else if (status === "ci_made") {
            text = "Mietvertrag:";
        }

        return text;
    };

    const wizardAcceptReservation = () => {
        setWizardAcceptBookingIsLoading(true);
        DispoAcceptBookingMutation(
            bookingInfoOverlayItem.bookingId,
            (err) => {
                if (err) {
                    console.error(err);
                    setWizardAcceptBookingIsLoading(false);
                    displayMessage(
                        "error",
                        t("messages.accept_booking_failure")
                    );
                } else {
                    setWizardAcceptBookingIsLoading(false);
                    displayMessage(
                        "success",
                        t("messages.accept_booking_success")
                    );
                }
            }
        );
    };
    const wizardRejectReservation = () => {
        setWizardRejectBookingIsLoading(true);
        DispoRejectBookingMutation(
            bookingInfoOverlayItem.bookingId,
            (err) => {
                if (err) {
                    console.error(err);
                    setWizardRejectBookingIsLoading(false);
                    displayMessage(
                        "error",
                        t("messages.reject_booking_failure")
                    );
                } else {
                    setWizardRejectBookingIsLoading(false);
                    displayMessage(
                        "success",
                        t("messages.reject_booking_success")
                    );
                }
            }
        );
    };

    const renderContent = () => {
        return (
            <QueryRenderer<BookingInfoModalQuery>
                //@ts-ignore
                environment={relayAuthEnvironment() as Environment}
                query={graphql`
                    query BookingInfoModalQuery($bookingId: String, $date: String) {
                        lastBookingStatus(bookingId: $bookingId, date: $date) {
                            bookingStatus
                            lastBooking
                        }
                    }
                `}
                cacheConfig={{force: true}}
                variables={{
                    // @ts-ignore
                    bookingId: predefinedBookingId,
                    date: predefinedStartdate
                }}
                render={(result) => {
                    if (!result.props) {
                        return (
                            <div style={{textAlign: "center", width: "100%"}}>
                                <CircularProgress/>
                            </div>
                        );
                    }

                    const lastBooking = {
                        lastBooking: result.props?.lastBookingStatus?.lastBooking,
                        bookingStatus: result.props?.lastBookingStatus?.bookingStatus
                    };

                    return (
                        <>
                            {renderBookingCIModal(lastBooking)}
                            <DialogActions style={{justifyContent: "flex-start"}}>
                                <div className={classes.wrapper}>
                                    <Tooltip
                                        title={t(
                                            "disposition.controls.tooltip.showBooking"
                                        )}
                                    >
                                        <Button
                                            startIcon={<VisibilityIcon/>}
                                            onClick={handleShowBooking}
                                            color="secondary"
                                            style={{marginLeft: -15}}
                                            size={"small"}
                                            variant={"outlined"}
                                        >
                                            {t("general.show")}
                                        </Button>
                                    </Tooltip>
                                </div>
                                {modules.WIZARD &&
                                    bookingInfoOverlayItem.status === "reservation" &&
                                    !isSpezRes(
                                        bookingInfoOverlayItem.reservationNumber
                                    ) && (
                                        <>
                                            <div className={classes.wrapper}>
                                                <Button
                                                    startIcon={<CheckIcon/>}
                                                    onClick={wizardAcceptReservation}
                                                    type="submit"
                                                    size={"small"}
                                                    variant="outlined"
                                                    color="secondary"
                                                    disabled={wizardAcceptBookingIsLoading}
                                                >
                                                    {t("bookingLine.confirmInWizard")}
                                                </Button>
                                                {wizardAcceptBookingIsLoading && (
                                                    <CircularProgress
                                                        size={24}
                                                        className={classes.buttonProgress}
                                                    />
                                                )}
                                            </div>
                                            <div className={classes.wrapper}>
                                                <Button
                                                    startIcon={<RemoveCircleIcon/>}
                                                    onClick={wizardRejectReservation}
                                                    color="secondary"
                                                    size={"small"}
                                                    variant={"outlined"}
                                                    disabled={wizardRejectBookingIsLoading}
                                                >
                                                    {t("bookingLine.declineInWizard")}
                                                </Button>
                                                {wizardRejectBookingIsLoading && (
                                                    <CircularProgress
                                                        size={24}
                                                        className={classes.buttonProgress}
                                                    />
                                                )}
                                            </div>
                                        </>
                                    )}
                                {(bookingInfoOverlayItem.status === "reservation" ||
                                        bookingInfoOverlayItem.status === "transfer_res") &&
                                    !modules.WIZARD && (
                                        <>
                                            <div className={classes.wrapper}>
                                                <Tooltip
                                                    title={
                                                        !checkoutIsAllowed
                                                            ? "Keine Berechtigung"
                                                            : ""
                                                    }
                                                >
                                        <span>
                                            <Button
                                                startIcon={<CheckIcon/>}
                                                size={"small"}
                                                onClick={() => {
                                                    setAcceptReservationEmailModalIsOpen(
                                                        true
                                                    );
                                                }}
                                                color="secondary"
                                                disabled={!checkoutIsAllowed}
                                                variant={"outlined"}
                                            >
                                                {t("bookingLine.confirm")}
                                            </Button>
                                        </span>
                                                </Tooltip>
                                            </div>
                                            <div className={classes.wrapper}>
                                                <Tooltip
                                                    title={
                                                        !checkoutIsAllowed
                                                            ? "Keine Berechtigung"
                                                            : ""
                                                    }
                                                >
                                        <span>
                                            <Button
                                                startIcon={<RemoveCircleIcon/>}
                                                onClick={() => {
                                                    setRejectReservationEmailModalIsOpen(
                                                        true
                                                    );
                                                }}
                                                size={"small"}
                                                color="secondary"
                                                disabled={!checkoutIsAllowed}
                                                variant={"outlined"}
                                            >
                                                {t("bookingLine.decline")}
                                            </Button>
                                        </span>
                                                </Tooltip>
                                            </div>
                                            {bookingInfoOverlayItem?.hasVehicle && !modules.WIZARD && (
                                                <div className={classes.wrapper}>
                                                    <Tooltip
                                                        title={
                                                            !checkoutIsAllowed
                                                                ? "Keine Berechtigung"
                                                                : ""
                                                        }
                                                    >
                                            <span>
                                                <Button
                                                    startIcon={<Sync/>}
                                                    onClick={() => {
                                                        if (
                                                            bookingInfoOverlayItem.status ===
                                                            "transfer_res"
                                                        ) {
                                                            if (lastBooking?.lastBooking && lastBooking?.bookingStatus != "ci_made") {
                                                                setBookingCIModalIsOpen(true);
                                                                return;
                                                            }

                                                            history.push(
                                                                `/manager/transfer-reservation/${bookingInfoOverlayItem.bookingId}/r2m`
                                                            );
                                                        } else {
                                                            if (lastBooking?.lastBooking && lastBooking?.bookingStatus != "ci_made") {
                                                                setBookingCIModalIsOpen(true);
                                                                return;
                                                            }

                                                            history.push(
                                                                `/manager/reservation/${bookingInfoOverlayItem.bookingId}/r2m`
                                                            );
                                                        }
                                                        dispatch({
                                                            type: CLOSE_BOOKING_INFO,
                                                        });
                                                    }}
                                                    color="secondary"
                                                    size={"small"}
                                                    variant={"outlined"}
                                                    disabled={
                                                        !checkoutIsAllowed
                                                    }
                                                >
                                                    {t("bookingLine.convert")}
                                                </Button>
                                            </span>
                                                    </Tooltip>
                                                </div>
                                            )}
                                        </>
                                    )}

                                {(bookingInfoOverlayItem.status === "contract_co" ||
                                    bookingInfoOverlayItem.status === "transfer_co") && !modules.WIZARD && (
                                    <>
                                        <div className={classes.wrapper}>
                                            <Button
                                                startIcon={<VpnKey/>}
                                                onClick={() => {
                                                    dispatch({type: CLOSE_BOOKING_INFO});

                                                    if (
                                                        bookingInfoOverlayItem.status ===
                                                        "transfer_co"
                                                    ) {
                                                        history.push(
                                                            `/manager/transfer/${bookingInfoOverlayItem.bookingId}/ci`
                                                        );
                                                    } else {
                                                        history.push(
                                                            `/manager/contract/${bookingInfoOverlayItem.bookingId}/ci`
                                                        );
                                                    }
                                                }}
                                                size={"small"}
                                                color="secondary"
                                                variant={"outlined"}
                                            >
                                                {t("bookingLine.checkIn")}
                                            </Button>
                                        </div>
                                    </>
                                )}
                                {(bookingInfoOverlayItem.status === "contract_co" ||
                                        bookingInfoOverlayItem.status === "transfer_co") &&
                                    bookingInfoOverlayItem.wasReservation &&
                                    !bookingInfoOverlayItem.hasInvoice &&
                                    rights?.find(
                                        (item: any) => item.right === "VORG_SET_RES"
                                    ) && (
                                        <>
                                            <div className={classes.wrapper}>
                                                <Button
                                                    startIcon={<UndoOutlined/>}
                                                    onClick={() => {
                                                        dispatch({type: CLOSE_BOOKING_INFO});

                                                        UndoCheckOutMutation(
                                                            {bookingId: bookingInfoOverlayItem.bookingId.toString()},
                                                            (err) => {
                                                                if (err) {
                                                                    console.error(err);
                                                                    displayMessage(
                                                                        "error",
                                                                        t("messages.undoCheckoutFailure")
                                                                    );
                                                                } else {
                                                                    displayMessage(
                                                                        "success",
                                                                        t("messages.undoCheckoutSuccess")
                                                                    );
                                                                    dispatch({
                                                                        type: CLOSE_BOOKING_INFO,
                                                                    });
                                                                    dispatch({
                                                                        type: FETCHING_DATA_START,
                                                                        payload: {
                                                                            startdate: dispositionStartdate,
                                                                            enddate: dispositionEnddate,
                                                                            selectedStation: selectedStation,
                                                                        },
                                                                    });
                                                                }

                                                            }
                                                        );
                                                    }}
                                                    size={"small"}
                                                    color="secondary"
                                                    variant={"outlined"}
                                                >
                                                    {t("bookingLine.undoCheckout")}
                                                </Button>
                                            </div>
                                        </>
                                    )}
                                {(bookingInfoOverlayItem.status === "ci_made" ||
                                        bookingInfoOverlayItem.status === "transfer_ci") &&
                                    !bookingInfoOverlayItem.isBilled &&
                                    rights?.find(
                                        (item: any) => item.right === "VORG_SET_CO"
                                    ) && (
                                        <>
                                            <div className={classes.wrapper}>
                                                <Button
                                                    startIcon={<UndoOutlined/>}
                                                    onClick={() => {
                                                        dispatch({type: CLOSE_BOOKING_INFO});

                                                        UndoCheckInMutation(
                                                            {bookingId: bookingInfoOverlayItem.bookingId.toString()},
                                                            (err) => {
                                                                if (err) {
                                                                    console.error(err);
                                                                    displayMessage(
                                                                        "error",
                                                                        t("messages.undoCheckinFailure")
                                                                    );
                                                                } else {
                                                                    displayMessage(
                                                                        "success",
                                                                        t("messages.undoCheckinSuccess")
                                                                    );
                                                                    dispatch({
                                                                        type: CLOSE_BOOKING_INFO,
                                                                    });
                                                                    dispatch({
                                                                        type: FETCHING_DATA_START,
                                                                        payload: {
                                                                            startdate: dispositionStartdate,
                                                                            enddate: dispositionEnddate,
                                                                            selectedStation: selectedStation,
                                                                        },
                                                                    });
                                                                }
                                                            }
                                                        );
                                                    }}
                                                    size={"small"}
                                                    color="secondary"
                                                    variant={"outlined"}
                                                >
                                                    {t("bookingLine.undoCheckin")}
                                                </Button>
                                            </div>
                                        </>
                                    )}
                            </DialogActions>
                            <DialogContent>
                                <Grid container spacing={0} style={{marginBottom: 10}}>
                                    <Grid item xs={4}>
                                        {t("bookingLine.bookingId")}:
                                    </Grid>
                                    <Grid item xs={8}>
                                        <b>{bookingInfoOverlayItem.bookingId}</b>
                                    </Grid>
                                    <Grid item xs={4}>
                                        {t("bookingLine.reservationNumber")}:
                                    </Grid>
                                    <Grid item xs={8}>
                                        <b>{bookingInfoOverlayItem.reservationNumber}</b>
                                    </Grid>
                                </Grid>
                                <Grid container spacing={0} style={{marginBottom: 10}}>
                                    <Grid item xs={4}>
                                        {t("bookingLine.customerId")}:
                                    </Grid>
                                    <Grid item xs={8}>
                                        <b>{bookingInfoOverlayItem.customer?.customerId}</b>
                                    </Grid>
                                    <Grid item xs={4}>
                                        {t("bookingLine.customer")}:
                                    </Grid>
                                    <Grid item xs={8}>
                                        <b>{bookingInfoOverlayItem.customer?.name1}</b>
                                    </Grid>
                                </Grid>
                                <Grid container spacing={0} style={{marginBottom: 10}}>
                                    <Grid item xs={4}>
                                        {t("bookingLine.reservedGroup")}:
                                    </Grid>
                                    <Grid item xs={8}>
                                        <b>
                                            {
                                                bookingInfoOverlayItem.vehicleGroup
                                                    ?.vehicleGroupId
                                            }{" "}
                                            -{" "}
                                            {
                                                bookingInfoOverlayItem.vehicleGroup
                                                    ?.description
                                            }
                                        </b>
                                    </Grid>
                                </Grid>
                                <Grid container spacing={0} style={{marginBottom: 10}}>
                                    <Grid item xs={4}>
                                        {t("bookingLine.departure")}:
                                    </Grid>
                                    <Grid item xs={8}>
                                        <b>
                                            {bookingInfoOverlayItem?.startstation?.stationId || bookingInfoOverlayItem?.startstation}{" "}
                                            -{" "}
                                            {moment(
                                                bookingInfoOverlayItem.startdate
                                            ).format("DD.MM.YYYY HH:mm")}
                                        </b>
                                    </Grid>
                                    {bookingInfoOverlayItem.enddate &&
                                    bookingInfoOverlayItem.endstation ? (
                                        <>
                                            <Grid item xs={4}>
                                                {t("bookingLine.arrival")}:
                                            </Grid>
                                            <Grid item xs={8}>
                                                <b>
                                                    {
                                                        bookingInfoOverlayItem?.endstation?.stationId ||
                                                        bookingInfoOverlayItem?.endstation
                                                    }{" "}
                                                    -{" "}
                                                    {moment(
                                                        bookingInfoOverlayItem.enddate
                                                    ).format("DD.MM.YYYY HH:mm")}
                                                </b>
                                            </Grid>
                                        </>
                                    ) : (
                                        <>
                                            <Grid item xs={4}>
                                                {t("bookingLine.plannedArrival")}:
                                            </Grid>
                                            <Grid item xs={8}>
                                                <b>
                                                    {
                                                        bookingInfoOverlayItem
                                                            ?.plannedEndstation
                                                            ?.stationId ||
                                                        bookingInfoOverlayItem
                                                            ?.plannedEndstation
                                                    }{" "}
                                                    -{" "}
                                                    {moment(
                                                        bookingInfoOverlayItem.plannedEnddate
                                                    ).format("DD.MM.YYYY HH:mm")}
                                                </b>
                                            </Grid>
                                        </>
                                    )}
                                    <Grid item xs={4}>
                                        {t("bookingLine.delivery")}:
                                    </Grid>
                                    <Grid item xs={8}>
                                        {bookingInfoOverlayItem.deliveryDate ? (
                                            <b>
                                                {moment(
                                                    bookingInfoOverlayItem.deliveryDate
                                                ).format("DD.MM.YYYY HH:mm")}
                                            </b>
                                        ) : (
                                            <b>/</b>
                                        )}
                                    </Grid>
                                    <Grid item xs={4}>
                                        {t("bookingLine.pickUp")}:
                                    </Grid>
                                    <Grid item xs={8}>
                                        {bookingInfoOverlayItem.pickupDate ? (
                                            <b>
                                                {moment(
                                                    bookingInfoOverlayItem.pickupDate
                                                ).format("DD.MM.YYYY HH:mm")}
                                            </b>
                                        ) : (
                                            <b>/</b>
                                        )}
                                    </Grid>
                                </Grid>
                                <Grid container spacing={0} style={{marginBottom: 10}}>
                                    <Grid item xs={4}>
                                        {t("bookingLine.rate")}:
                                    </Grid>
                                    <Grid item xs={8}>
                                        <b>{bookingInfoOverlayItem.rate}</b>
                                    </Grid>
                                    <Grid item xs={4}>
                                        {t("bookingLine.miscellaneous")}:
                                    </Grid>
                                    <Grid item xs={8}>
                                        <b>{bookingInfoOverlayItem.divdata}</b>
                                    </Grid>
                                </Grid>
                            </DialogContent>
                        </>
                    )
                }}
            />
        );
    }

    const renderAcceptReservationEmailModal = () => {
        if (!acceptReservationEmailModalIsOpen) {
            return <></>;
        }

        return (
            <Suspense fallback={<></>}>
                <EmailModalWrapper
                    open={true}
                    handleClose={() => {
                        setAcceptReservationEmailModalIsOpen(false);
                    }}
                    bookingId={bookingInfoOverlayItem.bookingId}
                    type={"RB"}
                    openPrintMenu={true}
                />
            </Suspense>
        );
    };

    const renderBookingCIModal = (lastBooking) => {
        if (!bookingCIModalIsOpen) {
            return <></>;
        }

        return <PreviousContractNotCheckedInModal open={bookingCIModalIsOpen}
                                                  handleClose={() => {
                                                      setBookingCIModalIsOpen(false)
                                                  }}
                                                  bookingId={lastBooking?.lastBooking}
                                                  type={lastBooking?.bookingStatus}
        />
    }

    const renderRejectReservationEmailModal = () => {
        if (!rejectReservationEmailModalIsOpen) {
            return <></>;
        }

        return (
            <Suspense fallback={<></>}>
                <EmailModalWrapper
                    open={true}
                    handleClose={() => {
                        setRejectReservationEmailModalIsOpen(false);
                    }}
                    bookingId={bookingInfoOverlayItem.bookingId}
                    type={"RA"}
                    openPrintMenu={false}
                />
            </Suspense>
        );
    };

    const handleShowBooking = () => {
        const bookingType = bookingInfoOverlayItem.status;
        logger.log("show booking", {
            bookingId: bookingInfoOverlayItem.bookingId,
            bookingType: bookingType,
        });
        dispatch({type: CLOSE_BOOKING_INFO});

        if (bookingType === "reservation") {
            history.push(
                `/manager/reservation/${bookingInfoOverlayItem.bookingId}/show`
            );

            return;
        } else if (
            bookingType === "transfer_co" ||
            bookingType === "transfer_ci"
        ) {
            history.push(
                `/manager/transfer/${bookingInfoOverlayItem.bookingId}/show`
            );

            return;
        } else if (bookingType === "transfer_res") {
            history.push(
                `/manager/transfer-reservation/${bookingInfoOverlayItem.bookingId}/show`
            );

            return;
        } else if (bookingType === "contract_co" || bookingType === "ci_made") {
            history.push(
                `/manager/contract/${bookingInfoOverlayItem.bookingId}/show`
            );

            return;
        }

        throw new BaseError("Unknown booking type", {
            context: {
                bookingType: bookingType,
            },
        });
    };

    return (
        <>
            {renderAcceptReservationEmailModal()}
            {renderRejectReservationEmailModal()}
            <Dialog
                onClose={() => {
                    dispatch({type: CLOSE_BOOKING_INFO});
                }}
                aria-labelledby="customized-dialog-title"
                open={true}
                maxWidth={"lg"}
            >
                <AppBar className={classes.appBar}>
                    <Toolbar>
                        <Typography variant="h6" className={classes.title}>
                            {getHeaderText()}{" "}
                            {bookingInfoOverlayItem.bookingId}
                        </Typography>
                        <IconButton
                            aria-label="close"
                            data-testid={"closeBookingInfo"}
                            onClick={() => {
                                dispatch({type: CLOSE_BOOKING_INFO});
                            }}
                        >
                            <CloseIcon/>
                        </IconButton>
                    </Toolbar>
                </AppBar>
                {renderContent()}
            </Dialog>
        </>
    );
};

export default BookingInfoModal;
